import React, { useEffect, useState } from 'react'
import SsoSection from '../../components/sso/SsoSection'
import { useNavigate,  useParams } from 'react-router-dom'
import { Mail, Lock } from 'lucide-react'
import { toast } from '../../lib/toast'
import styles from './Login.module.css'
import { useAuthStore } from '../../store/authStore'
import { validateEmail, sanitizeUrlParam } from '../../utils/security'
import { UserRole } from '../../enums/userRole'
import SecureInput from '../../ui/SecureInput/SecureInput'
import { useAccountBlock } from '../../hooks/useAccountBlock'
import logoIcon from '../../assets/logo_icon.png'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const { login } = useAuthStore(state => state)
  const navigate = useNavigate();
  const { error } = useParams();
  const { timeRemaining, isBlocked, handleError, clearBlock, checkBeforeSubmit } = useAccountBlock()


  useEffect(() => {
    if (error) {
      const sanitizedError = sanitizeUrlParam(error);
      toast.error(sanitizedError || 'An error occurred');
    }
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Check if account is still blocked
    if (!checkBeforeSubmit()) {
      return
    }

    // Validate email
    const emailValidation = validateEmail(email)
    if (!emailValidation.valid) {
      toast.error(emailValidation.error || 'Invalid email')
      return
    }

    // For login, we don't validate password strength - just check it's not empty
    if (!password.trim()) {
      toast.error('Password is required')
      return
    }

    setLoading(true)

    try {
      const loginResult = await login(emailValidation.sanitized, password)
      if (loginResult.success) {
        // Clear blocked state on successful login
        clearBlock()

        const roleId = loginResult.user?.role?.id
        if (roleId === UserRole.Pending) {
          navigate('/pending')
          toast.success('Your account is pending approval. You will get access once an administrator approves it.')
        } 
        if(roleId === UserRole.Viewer) {
          navigate('/toolslist')
          toast.success('Welcome back!')
        } else {
          navigate('/dashboard')
          toast.success('Welcome back!')
        }
      }
    } catch (err: any) {
      // Extract error message from server response
      const errorMessage = err.message || 'Unable to connect to server. Please try again.'
      const status = err.status || 0
      const userRole = err.userRole

      // Check if account is permanently blocked (userRole: 5)
      if (userRole === UserRole.Blocked) {
        handleError(err)
        // Redirect to pending page to show blocked message
        navigate('/pending', { state: { blocked: true, message: errorMessage } })
        return
      }

      // Handle temporary block errors
      handleError(err)

      // Show error message
      if (status === 401 || err.message.includes('401') || err.message.includes('Unauthorized')) {
        toast.error(errorMessage)
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }



  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.logo}>
            <img src={logoIcon} alt="Logo" className={styles.logoImage} />
          </div>
          <h1>{'Oitools Studio'}</h1>
          <p>{'Sign in to your account'}</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <SecureInput
            fieldType="email"
            label="Email Address"
            icon={<Mail size={16} />}
            value={email}
            onChange={(value) => setEmail(value)}
            placeholder="Enter your email"
            disabled={loading}
            className={styles.input}
            required
            name="email"
            autoComplete="email"
          />

          <SecureInput
            fieldType="password"
            label="Password"
            icon={<Lock size={16} />}
            value={password}
            onChange={(value) => setPassword(value)}
            placeholder="Enter your password"
            disabled={loading}
            className={styles.input}
            required
            name="password"
            autoComplete="current-password"
            skipPasswordValidation={true}
          />

          <div className={styles.actions}>
            <button
              type="submit"
              disabled={loading || !email.trim() || !password.trim() || isBlocked}
              className={styles.button}
            >
              {isBlocked
                ? `Try again in ${timeRemaining}`
                : loading
                  ? 'Signing in...'
                  : 'Sign In'}
            </button>
          </div>
        </form>

        <SsoSection loading={loading} fromLogin={true} />
        {/* <div className={styles.footer}>
          <p>
            Don't have an account?{' '}
            <Link to="/register" className={styles.link}>
              Sign up
            </Link>
          </p>
        </div> */}
      </div>
    </div>
  )
}
